import * as React from 'react';
import {useDispatch} from 'react-redux';

import {Button} from 'shells/button/button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {AnimatingBookmark} from 'shells/onboarding-checklist/animating-bookmark';
import {ChecklistCard} from 'shells/onboarding-checklist/checklist-card';
import {OnboardingChecklistHeader} from 'shells/onboarding-checklist/onboarding-checklist-header';
import {Body} from 'shells/typography/body';

import {useDismissibleUserSetting} from '../../../util/hooks/use-dismissible-user-setting';

import {CueCardStack} from '../../glossary/layout/cue-card-stack';
import {useGetUpcomingWebcasts} from '../../master-dashboard/dash-grid/cards/graphql/hooks/use-get-upcoming-webcasts';
import {OpenableModals, useOpenableModal} from '../../openable-modals';

import {useGetOnboardingChecklist} from '../graphql/hooks/use-get-onboarding-checklist';

import {getOnboardingChecklistItems, useGetCueCardsForOnboardingSidebar} from './helpers';
import {toggleSidebarDismissed} from './redux/onboarding-actions';
import {UpcomingWebcastCueCard} from './upcoming-webcast-cue-card';

import styles from './onboarding-sidebar-content.module.css';

const HAS_DISMISSED_ONBOARDING_SIDEBAR = 'has_dismissed_onboarding_sidebar';
const WEBCAST_TYPE = 'Intro to Nutshell';

type Props = {
    closeSidebar: () => void;
};

export function OnboardingSidebarContent(props: Props) {
    const {
        onboardingChecklist,
        completedCount,
        totalCount,
        checklistIsComplete,
        isLoading: checklistIsLoading,
    } = useGetOnboardingChecklist();
    const {shouldShow, onDismiss} = useDismissibleUserSetting(HAS_DISMISSED_ONBOARDING_SIDEBAR);
    const preventDismissingSidebar = NutClientConfig.isTrial;
    const [isShowingChecklist, setIsShowingChecklist] = React.useState<boolean>(
        !checklistIsComplete
    );
    const dispatch = useDispatch();
    const {webcasts, isLoading} = useGetUpcomingWebcasts(WEBCAST_TYPE);
    const cueCards = useGetCueCardsForOnboardingSidebar();

    const {onOpen: onOpenInviteUsersModal} = useOpenableModal(OpenableModals.INVITE_USERS_MODAL);
    const {onOpen: onOpenImportModal} = useOpenableModal(OpenableModals.IMPORT_MODAL);
    const onDismissSidebar = () => {
        dispatch(toggleSidebarDismissed(true));

        if (onDismiss) {
            onDismiss();
        }

        props.closeSidebar();
    };

    if (checklistIsLoading) {
        return <LoadingPage padding={4} />;
    }

    if (!shouldShow || !onboardingChecklist || !onboardingChecklist.isAvailable) {
        return null;
    }

    const checklist = {
        ...onboardingChecklist,
        items: getOnboardingChecklistItems({
            items: onboardingChecklist.items,
            openInviteModal: onOpenInviteUsersModal,
            openImportModal: onOpenImportModal,
            closeSidebar: props.closeSidebar,
        }),
    } as const;

    return (
        <div className={styles.container}>
            <AnimatingBookmark />
            <OnboardingChecklistHeader
                checklist={checklist}
                expandProps={{
                    isExpanded: isShowingChecklist,
                    onToggleExpand: () => setIsShowingChecklist(!isShowingChecklist),
                }}
                completedCount={completedCount || 0}
                totalCount={totalCount || 0}
                checklistIsComplete={checklistIsComplete || false}
            />
            {isShowingChecklist ? (
                <StackLegacy spacing={8}>
                    {checklist.items.map((item) => (
                        <ChecklistCard
                            key={item.key}
                            title={item.title}
                            subtitle={item.subtitle}
                            isComplete={item.isComplete}
                            onClick={item.onClick}
                        />
                    ))}
                </StackLegacy>
            ) : undefined}
            <div className={styles.explore}>
                <div className='mb-8'>
                    <Body size='large' isBold={true}>
                        Explore even more
                    </Body>
                </div>
                {isLoading ? (
                    <LoadingPage />
                ) : (
                    <div className='mb-16'>
                        <UpcomingWebcastCueCard webcasts={webcasts} />
                        <CueCardStack
                            cards={cueCards}
                            afterOnClickCta={props.closeSidebar}
                            noBookmark={true}
                        />
                    </div>
                )}
            </div>
            {isLoading || preventDismissingSidebar ? undefined : (
                <div className='mb-16'>
                    <Button onClick={onDismissSidebar} isFullWidth={true} size='big'>
                        Don’t show this sidebar anymore
                    </Button>
                </div>
            )}
        </div>
    );
}
